import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type { EhsFormioReactProps, FormioBuilderSchema } from '../';
import { EhsFormioReactComponent } from '../';
import { intl } from '../../../../lib';
import type { LanguageProperty } from '../../../generated/LanguageProperty';
import type { Component } from '@formio/react';
import { Radio, RadioGroup, TextInput } from '../../../form';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';
import { HBox } from '../../../layout';
import type {
  EhsFormioRadioSettings,
  EhsFormioRadioConstructor,
  RadioOption
} from './EhsFormioRadioCommon';
import {
  isRadioValueString,
  isRadioValueRadioOption,
  getOrBuildDefaultRadioOption
} from './EhsFormioRadioCommon';
import {
  OTHER_LABEL,
  OTHER_LABEL_TRANSLATED
} from '../checkboxgroup/EhsFormioCheckboxGroupUtil';
import { isEmpty } from 'lodash';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
/*
 * This component supports the open-choice FHIR Questionnaire item
 * It is similar to {@link EhsFormioRadio}, but includes an additional other item,
 * where any text value can be inputted.
 *
 * For the other item:
 *  radioOption.label = [ other TextInput ]
 *  radioOption.value = 'common.other'
 *
 * */
export const EhsFormioOpenChoiceRadioType = 'ehsOpenChoiceRadio';

export class EhsFormioOpenChoiceRadioButtons extends EhsFormioReactComponent<
  EhsFormioRadioSettings,
  EhsFormioRadioConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioOpenChoiceRadioType,
      label: 'common.unknown',
      key: EhsFormioOpenChoiceRadioType,
      classNames: '',
      classNamesLabel: '',
      classNamesRadios: '',
      radioOptions: [],
      ...extend
    });
  }

  //Begin Non static definitions-------------------------------------
  private classNames = '';
  private classNamesLabel = '';
  private classNamesRadios = '';
  private radioOptions = [{ radioLabel: '', radioValue: '', default: false }];
  extractFields(component: Component<EhsFormioRadioConstructor>): void {
    this.classNames = component.classNames;
    this.classNamesLabel = component.classNamesLabel;
    this.classNamesRadios = component.classNamesRadios;
    this.radioOptions = component.radioOptions;
    // values passed from the dataGrid
    this.dataForSetting = this._data[component.key as string];
    this.shouldSetValue = true;
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioRadioSettings>) => JSX.Element,
    EhsFormioRadioSettings
  ] {
    return [
      OpenChoiceRadioButtonsCmp,
      {
        classNames: this.classNames,
        classNamesLabel: this.classNamesLabel,
        classNamesRadios: this.classNamesRadios,
        radioOptions: this.radioOptions,
        defaultValue: this.dataForSetting
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

interface RadioGroupOpenChoiceProps
  extends Omit<EhsFormioReactProps<EhsFormioRadioSettings>, 'value'> {
  value: string | RadioOption | null;
  isQuestionnaire: boolean;
  errorMessage?: string;
}

/**
 * The Logic for the Formio-Component.
 *
 * Radio buttons with an additional other option that when selected
 * allows the user to input custom text.
 *
 * Note: the component requires that radioValue does not change, therefore
 * we handle the custom text from the user as the radioLabel and radioValue is
 * set to 'common.other'.
 *
 * @param params formio {@link EhsFormioRadioSettings} properties
 */
function OpenChoiceRadioButtonsCmp({
  keyForComponent,
  ...props
}: Readonly<EhsFormioReactProps<EhsFormioRadioSettings>>): JSX.Element {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );

  return (
    <div className={props.settings.classNames ?? '' + 'ehs-grid container'}>
      <RadioGroupOpenChoice
        errorMessage={errorMessage}
        isQuestionnaire={isQuestionnaire}
        {...props}
      />
    </div>
  );
}

function RadioGroupOpenChoice({
  value,
  defaultValue,
  settings,
  isQuestionnaire,
  errorMessage,
  onChange,
  readOnly,
  label,
  required
}: Readonly<RadioGroupOpenChoiceProps>): JSX.Element {
  const otherOption: RadioOption = useMemo(() => {
    const option = settings.radioOptions.find(
      option => option.radioValue === OTHER_LABEL
    );

    if (!option) {
      throw new Error('Other option is required.');
    }

    // if radioLabel has been removed by the FMS, add it back
    // JSON serializer removes empty properties...
    if (option && !option.radioLabel) {
      option.radioLabel = '';
    }
    return option;
  }, [settings.radioOptions]);

  // filter out other option returned by FMS
  const filteredOptions = useMemo(
    () =>
      settings.radioOptions.filter(
        ({ radioValue }) => radioValue !== OTHER_LABEL
      ),
    [settings.radioOptions]
  );

  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [otherOptionValue, setOtherOptionValue] = useState(
    otherOption.radioLabel
  );

  const classNames = () => {
    if (settings.classNamesRadios) {
      return settings.classNamesRadios;
    } else {
      return 'ehs-grid grid-xs-11';
    }
  };

  const onChangeWrapper = useCallback(
    (selectedRadioValue: string, isOther: boolean = false) => {
      let option: RadioOption;

      // either the new text is passed, or the other label
      // if other is selected and text is passed, we update normally
      // if the other label is passed, we are setting the default
      if ((isOther && isOtherSelected) || selectedRadioValue === OTHER_LABEL) {
        // build a new other option, with the new value from the TextInput, and then pass to formio
        option = {
          radioValue: OTHER_LABEL,
          radioLabel:
            selectedRadioValue === OTHER_LABEL ? '' : selectedRadioValue,
          default: false
        };
      } else {
        option = filteredOptions.find(
          option => option.radioValue === selectedRadioValue
        ) as RadioOption;
        setIsOtherSelected(false);
        setOtherOptionValue('');
      }
      onChange((option as unknown) as string);
    },
    [filteredOptions, onChange, isOtherSelected]
  );

  const valueWrapper: string | null = useMemo(() => {
    // no formio value
    if (!value) {
      const defaultOption = getOrBuildDefaultRadioOption(
        settings.radioOptions,
        // take dataGrid values first if available
        settings.defaultValue ?? defaultValue
      );

      if (defaultOption && defaultOption.radioValue === OTHER_LABEL) {
        setIsOtherSelected(true);
        setOtherOptionValue(defaultOption.radioLabel);
      }
      return defaultOption?.radioValue ?? null;
    }

    if (isRadioValueString(value)) {
      return (
        getOrBuildDefaultRadioOption(settings.radioOptions, value)
          ?.radioValue ?? null
      );
    }

    if (isRadioValueRadioOption(value)) {
      if (value.radioValue === OTHER_LABEL) {
        return OTHER_LABEL;
      }
      return value.radioValue;
    }
    return value;
  }, [defaultValue, settings.defaultValue, settings.radioOptions, value]);

  const otherTextInputRef = useRef<HTMLInputElement>(null);
  // focus on other item TextInput when selected
  useEffect(() => {
    if (isOtherSelected && !isEmpty(value)) {
      otherTextInputRef.current?.focus();
    }
    // we only want to focus the text input when the other item is selected, not in draft mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOtherSelected]);

  // set the default formio value correctly
  useEffect(() => {
    // if no formio value, but the component already has a value
    // then force the onChangeWrapper to execute
    if (!value && valueWrapper) {
      if (valueWrapper === OTHER_LABEL) {
        onChangeWrapper(otherOption.radioLabel, true);
        return;
      }
      onChangeWrapper(valueWrapper);
    }
  }, [
    onChangeWrapper,
    otherOption.radioLabel,
    settings.radioOptions,
    value,
    valueWrapper
  ]);

  return (
    <RadioGroup
      value={valueWrapper}
      onChange={onChangeWrapper}
      readOnly={readOnly}
      className={classNames()}
      row={!isQuestionnaire}
      label={label}
      required={required}
      error={errorMessage}
      labelOnTop={isQuestionnaire}
    >
      {filteredOptions.map((radio: RadioOption) => {
        return (
          <Radio
            key={radio.radioValue}
            label={intl(radio.radioLabel as LanguageProperty, radio.radioLabel)}
            value={radio.radioValue}
            readOnly={readOnly}
          />
        );
      })}
      {otherOption && (
        <HBox>
          <Radio
            key={otherOption.radioLabel}
            label=""
            readOnly={readOnly}
            value={OTHER_LABEL}
            onChange={() => {
              setIsOtherSelected(true);
            }}
          />
          <TextInput
            ref={otherTextInputRef}
            label={OTHER_LABEL_TRANSLATED}
            type={'text'}
            readOnly={readOnly}
            disabled={readOnly && !isOtherSelected}
            required={readOnly ? false : isOtherSelected}
            value={otherOptionValue}
            onChange={newText => {
              // pass the text as radioLabel, since radioValue cannot change
              setOtherOptionValue(() => {
                onChangeWrapper(newText, true);
                return newText;
              });
            }}
            onFocus={() => {
              setIsOtherSelected(true);
              // force formio to select the other option
              // need to pass a RadioOption in order for formio to correctly update
              onChange(({
                radioValue: OTHER_LABEL,
                radioLabel: otherTextInputRef?.current?.value ?? '',
                default: false
              } as unknown) as string);
            }}
          />
        </HBox>
      )}
    </RadioGroup>
  );
}
